import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CreditCard, Settings2, Plus, Edit, Trash2, X, CheckCircle,
  ArrowUpRight, Shield, Star, Clock, AlertCircle, Settings,
  DollarSign, FileText, Users, Crown, Zap, Check, MapPin,
  Globe, Building2, Calendar, Save, Loader2, ChevronLeft, ChevronRight
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import SubscriptionPlanModal from "./SubscriptionPlanModal";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { SubscriptionPlanAreaAvailableTimeEnum, SubscriptionPlanAvailableTimeEnum } from "../../modules/shared/model";
import {
  fetchSubscriptionPlans,
  deleteSubscriptionPlan,
  clearSubscriptionPlansError,
  updateSubscriptionPlan,
  bulkUpdateSubscriptionPlanAreas
} from "../../redux/Slices/subscriptionPlanSlice";

const SubscriptionPlansadmin = () => {
  const dispatch = useAppDispatch();
  const { 
    plans, 
    plansLoading, 
    plansError, 
    plansTotalCount,
    plansHasNextPage,
    plansHasPreviousPage,
    plansCurrentPage,
    plansLimit
  } = useAppSelector((state) => state.subscriptionPlans);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [selectedRegion, setSelectedRegion] = useState("all");
  const [expandedPlans, setExpandedPlans] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Load subscription plans on component mount and when pagination changes
  useEffect(() => {
    dispatch(fetchSubscriptionPlans({ page: currentPage, limit: pageSize }));
  }, [dispatch, currentPage, pageSize]);

  // Clear errors when component mounts
  useEffect(() => {
    dispatch(clearSubscriptionPlansError());
  }, [dispatch]);

  const handleDeletePlan = async (planId: string) => {
    if (window.confirm("Are you sure you want to delete this subscription plan?")) {
      try {
        await dispatch(deleteSubscriptionPlan(planId)).unwrap();
        // Refresh the plans list
        dispatch(fetchSubscriptionPlans({ page: currentPage, limit: pageSize }));
      } catch (error) {
        console.error("Error deleting subscription plan:", error);
      }
    }
  };

  const handlePlanCreated = () => {
    // Refresh the plans list after successful creation
    dispatch(fetchSubscriptionPlans({ page: currentPage, limit: pageSize }));
  };

  const togglePlanExpansion = (planId: string) => {
    const newExpandedPlans = new Set(expandedPlans);

    if (expandedPlans.has(planId)) {
      newExpandedPlans.delete(planId);
    } else {
      newExpandedPlans.add(planId);
    }

    setExpandedPlans(newExpandedPlans);
  };

  const handleEditPlan = (plan: any) => {
    setShowEditModal(plan);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Calculate pagination info
  const totalPages = Math.ceil((plansTotalCount || 0) / pageSize);
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, plansTotalCount || 0);


  const PlanCard = ({ plan }) => (
    <Card className="p-6 space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-semibold">{plan.name}</h3>
            <Badge variant={undefined} className={
              Number(plan.type?.id) === 2 ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"
            }>
              {plan.type?.name || "Regular"}
            </Badge>
            {Number(plan.type?.id) === 2 && (
              <Badge variant={undefined} className="bg-orange-100 text-orange-700">
                <Clock className="h-3 w-3 mr-1" />
                {plan.availableTime.name}
              </Badge>
            )}
            <Badge variant={undefined} className="bg-green-100 text-green-700">
              Active
            </Badge>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <DollarSign className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">${plan.amount} / {plan.type?.name?.toLowerCase() || "regular"}</span>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <CreditCard className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">{plan.credits} credits</span>
          </div>
          {plan.discount > 0 && (
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-green-600 font-medium">Save {plan.discount}%</span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEditPlan(plan)}
          >
            <Edit className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-600"
            onClick={() => handleDeletePlan(plan.id)}
            disabled={plansLoading}
          >
            {plansLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => togglePlanExpansion(plan.id)}
            className="flex items-center gap-1"
          >
            <span className="text-sm">
              {expandedPlans.has(plan.id) ? "Hide Areas" : "Show Areas"}
            </span>
            <ArrowUpRight
              className={`h-4 w-4 transition-transform ${expandedPlans.has(plan.id) ? "rotate-180" : ""
                }`}
            />
          </Button>
        </div>
      </div>

      {/* Plan Details */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium mb-2">Plan Type</h4>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span className="text-sm">{plan.type?.name || "Regular"}</span>
          </div>
        </div>
        {Number(plan.type?.id) === 2 && plan.availableTime && (
          <div>
            <h4 className="font-medium mb-2">Available Time</h4>
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-400" />
              <span className="text-sm">
                {Number(plan.availableTime.id) === SubscriptionPlanAvailableTimeEnum.Day && "Day (6 AM - 6 PM)"}
                {Number(plan.availableTime.id) === SubscriptionPlanAvailableTimeEnum.Night && "Night (6 PM - 6 AM)"}
                {Number(plan.availableTime.id) === SubscriptionPlanAvailableTimeEnum.FullDay && "Full Day (24 Hours)"}
              </span>
            </div>
          </div>
        )}
        <div>
          <h4 className="font-medium mb-2">Created</h4>
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-400" />
            <span className="text-sm">
              {plan.createdAt ? new Date(plan.createdAt).toLocaleDateString() : "N/A"}
            </span>
          </div>
        </div>
      </div>

      {/* Plan Summary */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="font-medium mb-3">Plan Summary</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-sm text-gray-500">Base Amount</span>
            <p className="font-medium">${plan.amount}</p>
          </div>
          <div>
            <span className="text-sm text-gray-500">Base Credits</span>
            <p className="font-medium">{plan.credits}</p>
          </div>
          {plan.discount > 0 && (
            <div>
              <span className="text-sm text-gray-500">Discount</span>
              <p className="font-medium text-green-600">{plan.discount}%</p>
            </div>
          )}
        </div>
      </div>

      {/* Plan Areas */}
      {expandedPlans.has(plan.id) && (
        <div className="border-t pt-4">
          <h4 className="font-medium mb-3 flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Plan Areas ({plan.areas?.length || 0})
          </h4>

          {plan.areas?.length > 0 ? (
            <div className="space-y-3">
              {plan.areas.map((area, index) => (
                <div key={area.id} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="flex items-center gap-1">
                        <Globe className="h-3 w-3" />
                        {area.country?.name || "Unknown Country"}
                      </Badge>
                      {area.category ? (
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          {area.category.name}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="flex items-center gap-1 bg-gray-100 text-gray-600">
                          <Building2 className="h-3 w-3" />
                          All Categories
                        </Badge>
                      )}
                      {area.availableTime && (
                        <Badge variant="outline" className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {area.availableTime.id === SubscriptionPlanAreaAvailableTimeEnum.Day && "Day (6 AM - 6 PM)"}
                          {area.availableTime.id === SubscriptionPlanAreaAvailableTimeEnum.Night && "Night (6 PM - 6 AM)"}
                          {area.availableTime.id === SubscriptionPlanAreaAvailableTimeEnum.FullDay && "Full Day (24 Hours)"}
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {/* TODO: Edit area */ }}
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600"
                        onClick={() => {/* TODO: Delete area */ }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-500">Credits</span>
                      <p className="font-medium">{area.credits}</p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Amount</span>
                      <p className="font-medium">${area.amount}</p>
                    </div>
                    {area.discount > 0 && (
                      <div>
                        <span className="text-sm text-gray-500">Discount</span>
                        <p className="font-medium text-green-600">{area.discount}%</p>
                      </div>
                    )}
                    {area.availableTime && (
                      <div>
                        <span className="text-sm text-gray-500">Available Time</span>
                        <p className="font-medium">
                          {Number(area.availableTime.id) === SubscriptionPlanAreaAvailableTimeEnum.Day && "Day (6 AM - 6 PM)"}
                          {Number(area.availableTime.id) === SubscriptionPlanAreaAvailableTimeEnum.Night && "Night (6 PM - 6 AM)"}
                          {Number(area.availableTime.id) === SubscriptionPlanAreaAvailableTimeEnum.FullDay && "Full Day (24 Hours)"}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500">
              <Globe className="h-8 w-8 mx-auto mb-2 text-gray-300" />
              <p>No areas configured for this plan</p>
            </div>
          )}
        </div>
      )}
    </Card>
  );


  return (
    <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      <AdminHeader />

      <main className="lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Subscription Plans</h1>
                <p className="text-gray-600 mt-1">Manage subscription plans and pricing for professionals</p>
              </div>
              <Button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Plan
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Total Plans</p>
                    <p className="text-2xl font-semibold mt-1">
                      {plansLoading ? (
                        <Loader2 className="h-6 w-6 animate-spin" />
                      ) : (
                        plansTotalCount || 0
                      )}
                    </p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <CreditCard className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Active Plans</p>
                    <p className="text-2xl font-semibold mt-1">
                      {plansLoading ? (
                        <Loader2 className="h-6 w-6 animate-spin" />
                      ) : (
                        `${plans.length} on page`
                      )}
                    </p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Total Revenue</p>
                    <p className="text-2xl font-semibold mt-1">
                      {plansLoading ? (
                        <Loader2 className="h-6 w-6 animate-spin" />
                      ) : (
                        `$${plans.reduce((sum, plan) => sum + (plan.amount || 0), 0).toFixed(2)}`
                      )}
                    </p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <DollarSign className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500">Total Credits</p>
                    <p className="text-2xl font-semibold mt-1">
                      {plansLoading ? (
                        <Loader2 className="h-6 w-6 animate-spin" />
                      ) : (
                        plans.reduce((sum, plan) => sum + (plan.credits || 0), 0)
                      )}
                    </p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <CreditCard className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <select
                  value={activeTab}
                  onChange={(e) => setActiveTab(e.target.value)}
                  className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                >
                  <option value="all">All Plans</option>
                  <option value="1">Regular Service Plans</option>
                  <option value="2">Emergency Service Plans</option>
                </select>
              </div>
              <select
                value={selectedRegion}
                onChange={(e) => setSelectedRegion(e.target.value)}
                className="px-4 py-2 border rounded-lg w-full sm:w-auto"
              >
                <option value="all">All Regions</option>
                <option value="canada">Canada</option>
                <option value="usa">United States</option>
                <option value="uk">United Kingdom</option>
              </select>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-4 py-2 border rounded-lg w-full sm:w-auto"
              >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
              </select>
            </div>

            {/* Error Display */}
            {plansError && (
              <Card className="p-4 border-red-200 bg-red-50">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="h-5 w-5" />
                  <div>
                    <p className="font-medium">Error loading subscription plans</p>
                    <p className="text-sm">{plansError}</p>
                  </div>
                </div>
              </Card>
            )}

            {/* Loading State */}
            {plansLoading && plans.length === 0 && (
              <Card className="p-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-400" />
                <p className="text-gray-500">Loading subscription plans...</p>
              </Card>
            )}

            {/* Plans Grid */}
            {!plansLoading && plans.length === 0 && !plansError && (
              <Card className="p-8 text-center">
                <CreditCard className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No subscription plans found</h3>
                <p className="text-gray-500 mb-4">Get started by creating your first subscription plan.</p>
                <Button onClick={() => setShowAddModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create First Plan
                </Button>
              </Card>
            )}

            {plans.length > 0 && (
              <>
                <div className="grid grid-cols-1 gap-6">
                  {plans
                    .filter(plan => activeTab === "all" || plan.type?.id?.toString() === activeTab)
                    .map(plan => (
                      <PlanCard key={plan.id} plan={plan} />
                    ))}
                </div>

                {/* Pagination */}
                {plansTotalCount && plansTotalCount > pageSize && (
                  <Card className="p-4">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                      {/* Page Size Selector */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">Show:</span>
                        <select
                          value={pageSize}
                          onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                          className="px-3 py-1 border rounded text-sm"
                        >
                          <option value={5}>5</option>
                          <option value={10}>10</option>
                          <option value={20}>20</option>
                          <option value={50}>50</option>
                        </select>
                        <span className="text-sm text-gray-600">per page</span>
                      </div>

                      {/* Pagination Info */}
                      <div className="text-sm text-gray-600">
                        Showing {startIndex} to {endIndex} of {plansTotalCount} plans
                      </div>

                      {/* Page Navigation */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={!plansHasPreviousPage || plansLoading}
                          className="flex items-center gap-1"
                        >
                          <ChevronLeft className="h-4 w-4" />
                          Previous
                        </Button>

                        {/* Page Numbers */}
                        <div className="flex items-center gap-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }

                            return (
                              <Button
                                key={pageNum}
                                variant={currentPage === pageNum ? "default" : "ghost"}
                                size="sm"
                                onClick={() => handlePageChange(pageNum)}
                                disabled={plansLoading}
                                className="w-8 h-8 p-0"
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                        </div>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={!plansHasNextPage || plansLoading}
                          className="flex items-center gap-1"
                        >
                          Next
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                )}
              </>
            )}

            {/* Add/Edit Modal */}
            <AnimatePresence>
              {(showAddModal || showEditModal) && (
                <SubscriptionPlanModal
                  isOpen={showAddModal || !!showEditModal}
                  onClose={() => {
                    setShowAddModal(false);
                    setShowEditModal(null);
                  }}
                  onSuccess={handlePlanCreated}
                  editPlan={showEditModal}
                />
              )}
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SubscriptionPlansadmin;